import { Type, Expose, Transform } from 'class-transformer';
import { DocumentSnapshot } from 'firebase/firestore';
import { _FirebaseDocument, anyToDate } from './Models';
import { DocRef } from './Models';
import { TenantPlan, TenantServiceKey } from './Tenant';

export class TenantCreationRequest extends _FirebaseDocument {
    @Expose()
    public status: 'requested' | 'pending' | 'done' | 'failed'
    @Expose()
    public name: string
    @Expose()
    public plan: string | null

    @Type(() => DocRef)
    @Expose()
    public tenant: DocRef | null

    collection(): string {
        return "tenant_creation_requests";
    }

    static fromFirebase(o: DocumentSnapshot): TenantCreationRequest | null {
        return _FirebaseDocument._fromFirebase<TenantCreationRequest>(TenantCreationRequest, o);
    }
}

// (legacy pricing plans config removed)

export enum PriceLookupKeys {
    // New credit-based plans (S/M/L — credit amounts are in Stripe metadata, not here)
    VEILLE = "veille",
    QMS_S = "qms_s",
    QMS_M = "qms_m",
    QMS_L = "qms_l",
    TD_S = "td_s",
    TD_M = "td_m",
    TD_L = "td_l",
    QMS_TD_S = "qms_td_s",
    QMS_TD_M = "qms_td_m",
    QMS_TD_L = "qms_td_l",
    TOPUP_S = "topup_s",
    TOPUP_M = "topup_m",
    TOPUP_L = "topup_l",

    // Legacy keys
    FREE_TRY = "free_try",
    PLATFORM_ACCESS_FREE = "platform_access_free",
    SINGLE_ASSESSMENT = "single_assessment",
    EARLY_ACCESS_PLATFORM_ACCESS = "ea_platform_access",
    EARLY_ACCESS_QMS = "ea_qms",
    EARLY_ACCESS_TD = "ea_td",
    PLATFORM_ACCESS_EVALUATOR = "platform_access_evaluator",
    QMS_EVALUATOR = "qms_evaluator",
    TD_EVALUATOR = "td_evaluator",
    PLATFORM_ACCESS_STUDENT = "platform_access_student",
    QMS_STUDENT = "qms_student",
    TD_STUDENT = "td_student",
    PLATFORM_ACCESS_MANAGER = "platform_access_manager",
    QMS_MANAGER = "qms_manager",
    TD_MANAGER = "td_manager",
}

// ---------------- Stripe Product/Price Models -----------------

export class ProductMetadata {
    @Expose()
    order?: string | null
    @Expose()
    en_title?: string | null
    @Expose()
    en_description?: string | null
    @Expose()
    en_marketing_features?: string | null
    @Expose()
    fr_title?: string | null
    @Expose()
    fr_description?: string | null
    @Expose()
    fr_marketing_features?: string | null
    @Expose()
    quota_recurrent_assessments?: number | null
    @Expose()
    quota_one_time_assessments?: number | null
    @Expose()
    quota_users?: number | null
    @Expose()
    quota_users_qms?: number | null
    @Expose()
    quota_users_td?: number | null
    // Credit-based plan metadata (from Stripe product)
    @Expose()
    mac_plan?: string | null
    @Expose()
    credits?: number | null
    @Expose()
    max_projects_qms?: number | null
    @Expose()
    max_projects_td?: number | null
    @Expose()
    has_qms?: string | null
    @Expose()
    has_td?: string | null
    @Expose()
    welcome_bonus_eligible?: string | null
}

export class StripeProduct extends _FirebaseDocument {
    @Expose()
    active?: boolean | null
    @Type(() => ProductMetadata)
    @Expose()
    metadata?: ProductMetadata | null
    @Expose()
    mac_active?: boolean | null

    collection(): string {
        return "stripe_products";
    }
    static fromFirebase(o: DocumentSnapshot): StripeProduct | null {
        return _FirebaseDocument._fromFirebase<StripeProduct>(StripeProduct, o);
    }
}

export class PriceMetadata {
    @Expose()
    mac_lookup_key?: PriceLookupKeys | string | null
    @Expose()
    credits?: number | null
}

export class PriceRecurring {
    @Expose()
    aggregate_usage?: 'sum' | string | null
    @Expose()
    interval?: 'month' | 'year' | string | null
    @Expose()
    interval_count?: number | null
    @Expose()
    meter?: unknown | null
    @Expose()
    trial_period_days?: number | null
    @Expose()
    usage_type?: 'metered' | 'licensed' | string | null
}

export class StripePrice extends _FirebaseDocument {
    @Expose()
    active?: boolean | null
    @Expose()
    billing_scheme?: 'per_unit' | string | null
    @Expose()
    currency?: 'eur' | string | null
    @Expose()
    description?: string | null
    @Expose()
    interval?: 'month' | 'year' | string | null
    @Expose()
    interval_count?: number | null
    @Type(() => PriceMetadata)
    @Expose()
    metadata?: PriceMetadata | null
    @Expose()
    product?: string | null
    @Type(() => PriceRecurring)
    @Expose()
    recurring?: PriceRecurring | null
    @Expose()
    tax_behavior?: 'unspecified' | 'exempt' | 'exclusive' | string | null
    @Expose()
    tiers?: unknown | null
    @Expose()
    tiers_mode?: 'graduated' | 'volume' | string | null
    @Expose()
    transform_quantity?: unknown | null
    @Expose()
    trial_period_days?: number | null
    @Expose()
    type?: 'recurring' | 'one_time' | string | null
    @Expose()
    unit_amount?: number | null

    collection(): string {
        return "prices";
    }
    static fromFirebase(o: DocumentSnapshot): StripePrice | null {
        return _FirebaseDocument._fromFirebase<StripePrice>(StripePrice, o);
    }
}

// ---------------- Pricing plan (new structure) -----------------

export class Pricing {
    @Expose()
    public quota_key?: TenantServiceKey | null
    @Expose()
    public price_lookup_key!: PriceLookupKeys | string
    @Expose()
    public price_id?: string | null
    @Type(() => StripePrice)
    @Expose()
    public price?: StripePrice | null
    @Expose()
    public product_id?: string | null
    @Type(() => StripeProduct)
    @Expose()
    public product?: StripeProduct | null
}

export class PricingPlan {
    @Expose()
    public plan: TenantPlan
    @Type(() => Pricing)
    @Expose()
    public data: Pricing[] = []

    public getByQuotaKey(quotaKey: TenantServiceKey): Pricing | null {
        for (const item of this.data) {
            if (item.quota_key === quotaKey) return item
        }
        return null
    }

    public getByPriceLookupKey(priceLookupKey: PriceLookupKeys | string): Pricing | null {
        for (const item of this.data) {
            if (item.price_lookup_key === priceLookupKey) return item
        }
        return null
    }

    public getByPriceId(priceId: string): Pricing | null {
        for (const item of this.data) {
            if (item.price_id === priceId) return item
        }
        return null
    }

    /**
     * Return the first subscription-tier Pricing entry (not a topup).
     * Currently returns S tier. Use getByPriceLookupKey() for specific tiers.
     */
    public getSubscriptionPricing(): Pricing | null {
        for (const item of this.data) {
            if (!item.price_lookup_key?.startsWith('topup_')) return item
        }
        return null
    }
}

export class PricingPlans extends _FirebaseDocument {
    @Type(() => PricingPlan)
    @Expose()
    public data: PricingPlan[] = []
    @Expose()
    public active: boolean = false

    collection(): string {
        return 'pricing_plans'
    }
    static fromFirebase(o: DocumentSnapshot): PricingPlans | null {
        return _FirebaseDocument._fromFirebase<PricingPlans>(PricingPlans, o);
    }

    public getByPlan(plan: TenantPlan | string): PricingPlan | null {
        for (const item of this.data) {
            if (item.plan === plan) return item
        }
        return null
    }

    public getPricingByLookupKey(priceLookupKey: PriceLookupKeys | string): Pricing | null {
        for (const plan of this.data) {
            const match = plan.getByPriceLookupKey(priceLookupKey)
            if (match) return match
        }
        return null
    }
}

// (legacy nested PricingPlans removed)

export class Subscription extends _FirebaseDocument {
    @Transform(anyToDate)
    @Expose()
    public cancel_at: Date | null
    @Expose()
    public cancel_at_period_end: boolean | null
    @Transform(anyToDate)
    @Expose()
    public canceled_at: Date | null
    @Transform(anyToDate)
    @Expose()
    public created: Date | null
    @Transform(anyToDate)
    @Expose()
    public current_period_end: Date | null
    @Transform(anyToDate)
    @Expose()
    public current_period_start: Date | null
    @Transform(anyToDate)
    @Expose()
    public ended_at: Date | null
    @Expose()
    public items: SubscriptionItem[] | null
    @Expose()
    public metadata: Record<string, unknown> | null
    @Type(() => DocRef)
    @Expose()
    public price: DocRef | null
    @Type(() => DocRef)
    @Expose()
    public prices: DocRef[] | null
    @Type(() => DocRef)
    @Expose()
    public product: DocRef | null
    @Expose()
    public quantity: number | null
    @Expose()
    public role: unknown | null
    @Expose()
    public status: 'trialing' | 'active' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'canceled' | 'unpaid' | 'paused' | string | null
    @Expose()
    public stripeLink: string | null
    @Transform(anyToDate)
    @Expose()
    public trial_end: Date | null
    @Transform(anyToDate)
    @Expose()
    public trial_start: Date | null

    collection(): string {
        return "subscriptions";
    }
    static fromFirebase(o: DocumentSnapshot): Subscription | null {
        return _FirebaseDocument._fromFirebase(Subscription, o);
    }
}

// ---------------- Subscription nested item types -----------------

export class SubscriptionItemPriceProduct {
    @Expose()
    active?: boolean | null
    @Expose()
    attributes?: unknown[] | null
    @Expose()
    created?: number | null
    @Expose()
    default_price?: string | null
    @Expose()
    description?: string | null
    @Expose()
    features?: unknown[] | null
    @Expose()
    id?: string | null
    @Expose()
    images?: unknown[] | null
    @Expose()
    livemode?: boolean | null
    @Expose()
    marketing_features?: unknown[] | null
    @Type(() => ProductMetadata)
    @Expose()
    metadata?: ProductMetadata | null
    @Expose()
    name?: string | null
    @Expose()
    object?: 'product' | string | null
    @Expose()
    package_dimensions?: unknown | null
    @Expose()
    shippable?: boolean | null
    @Expose()
    statement_descriptor?: string | null
    @Expose()
    tax_code?: string | null
    @Expose()
    unit_label?: string | null
    @Expose()
    updated?: number | null
    @Expose()
    url?: string | null
}

export class SubscriptionItemPrice {
    @Expose()
    active?: boolean | null
    @Expose()
    billing_scheme?: 'per_unit' | string | null
    @Expose()
    created?: number | null
    @Expose()
    currency?: 'eur' | string | null
    @Expose()
    custom_unit_amount?: unknown | null
    @Expose()
    id?: string | null
    @Expose()
    livemode?: boolean | null
    @Expose()
    lookup_key?: string | null
    @Expose()
    metadata?: Record<string, unknown> | null
    @Expose()
    nickname?: string | null
    @Expose()
    object?: 'price' | string | null
    @Type(() => SubscriptionItemPriceProduct)
    @Expose()
    product?: SubscriptionItemPriceProduct | null
    @Type(() => PriceRecurring)
    @Expose()
    recurring?: PriceRecurring | null
    @Expose()
    tax_behavior?: 'unspecified' | 'exempt' | 'exclusive' | string | null
    @Expose()
    tiers_mode?: 'graduated' | 'volume' | string | null
    @Expose()
    type?: 'recurring' | string | null
    @Expose()
    unit_amount?: number | null
    @Expose()
    unit_amount_decimal?: string | null
}

export class SubscriptionItemPlan {
    @Expose()
    active?: boolean | null
    @Expose()
    aggregate_usage?: 'sum' | string | null
    @Expose()
    amount?: number | null
    @Expose()
    amount_decimal?: string | null
    @Expose()
    billing_scheme?: 'per_unit' | string | null
    @Expose()
    created?: number | null
    @Expose()
    currency?: 'eur' | string | null
    @Expose()
    id?: string | null
    @Expose()
    interval?: 'month' | 'year' | string | null
    @Expose()
    interval_count?: number | null
    @Expose()
    livemode?: boolean | null
    @Expose()
    metadata?: Record<string, unknown> | null
    @Expose()
    meter?: unknown | null
    @Expose()
    nickname?: string | null
    @Expose()
    object?: 'plan' | string | null
    @Expose()
    product?: string | null
    @Expose()
    tiers_mode?: unknown | string | null
    @Expose()
    transform_usage?: unknown | null
    @Expose()
    trial_period_days?: number | null
    @Expose()
    usage_type?: 'licensed' | string | null
}

export class SubscriptionItem {
    @Expose()
    billing_thresholds?: unknown | null
    @Expose()
    created?: number | null
    @Expose()
    current_period_end?: number | null
    @Expose()
    current_period_start?: number | null
    @Expose()
    discounts?: unknown[] | null
    @Expose()
    id?: string | null
    @Expose()
    metadata?: Record<string, unknown> | null
    @Expose()
    object?: 'subscription_item' | string | null
    @Type(() => SubscriptionItemPlan)
    @Expose()
    plan?: SubscriptionItemPlan | null
    @Type(() => SubscriptionItemPrice)
    @Expose()
    price?: SubscriptionItemPrice | null
    @Expose()
    quantity?: number | null
    @Expose()
    subscription?: string | null
    @Expose()
    tax_rates?: unknown[] | null
}

// ---------------- Project creation request -----------------

export enum ProjectCreationRequestStatus {
    REQUESTED = 'requested',
    PENDING = 'pending',
    DONE = 'done',
    FAILED = 'failed',
}

export enum ProjectCreationRequestType {
    QMS = 'qms',
    TD = 'td',
    REGULATORY_MONITORING = 'regulatory_monitoring',
}

export enum ProjectCreationRequestErrorCode {
    PAYMENT_METHOD_REQUIRED = 'payment_method_required',
    UNKNOWN = 'unknown',
}

export class ProjectCreationRequest extends _FirebaseDocument {
    @Expose()
    public status: ProjectCreationRequestStatus
    @Expose()
    public name: string
    @Expose()
    public type: ProjectCreationRequestType
    @Expose()
    public members: string[]
    @Expose()
    public admins: string[]
    @Expose()
    public company_name: string

    @Type(() => DocRef)
    @Expose()
    public project: DocRef | null

    @Expose()
    public error_code?: ProjectCreationRequestErrorCode | null

    @Expose()
    public stripe_error_code?: string | null

    collection(): string {
        return "project_creation_requests";
    }
    static fromFirebase(o: DocumentSnapshot): ProjectCreationRequest | null {
        return _FirebaseDocument._fromFirebase<ProjectCreationRequest>(ProjectCreationRequest, o);
    }
}

// ---------------- Member invitations -----------------

export enum InvitationStatus {
    REQUESTED = 'requested',
    PENDING = 'pending',
    ACCEPT = 'accept',
    ACCEPTED = 'accepted',
    REJECT = 'reject',
    REJECTED = 'rejected',
    CANCEL = 'cancel',
    CANCELLED = 'cancelled',
    EXPIRED = 'expired',
    FAILED = 'failed',
}

export type TenantRole = 'admin' | 'member'
export class Invitation extends _FirebaseDocument {
    @Expose()
    public tenant_id: string
    @Expose()
    public tenant_name: string
    @Expose()
    public created_by: string
    @Expose()
    public created_by_email: string
    @Expose()
    public status: InvitationStatus
    @Expose()
    public email: string
    @Expose()
    public tenant_role: TenantRole

    @Type(() => DocRef)
    @Expose()
    public projects_member: DocRef[]
    @Type(() => DocRef)
    @Expose()
    public projects_admin: DocRef[]

    /**
     * MYAC-1176 — who authorised each project grant, keyed by project id.
     *
     * An invitation's project list is no longer written once at creation: any
     * company admin may attach a project later, and the backend re-checks the
     * grants when the invitee accepts. Without this map every project would be
     * checked against `created_by`, so a project attached by a *second* admin
     * would fail that check and sink the whole invitation.
     *
     * Tolerant reader: a project with no entry falls back to `created_by`,
     * which is exactly the pre-MYAC-1176 behaviour for invitations written
     * before this field existed.
     */
    @Expose()
    public projects_granted_by: Record<string, string> = {}

    @Transform(anyToDate)
    @Expose()
    public expire_at: Date

    collection(): string {
        return "invitations";
    }
    static fromFirebase(o: DocumentSnapshot): Invitation | null {
        return _FirebaseDocument._fromFirebase<Invitation>(Invitation, o);
    }
}

/** One project attached to an invitation. `none` means "not attached". */
export type InvitationProjectRole = 'none' | 'member' | 'admin'
export interface InvitationProjectGrant {
    id: string
    role: InvitationProjectRole
}

/**
 * The statuses in which an invitation's project grants can still be edited:
 * the invitee has not answered yet, so nothing has been turned into a real
 * membership. Once accepted, rejected, cancelled or expired, project access is
 * managed on the project itself instead.
 */
export const EDITABLE_INVITATION_STATUSES: InvitationStatus[] = [
    InvitationStatus.REQUESTED,
    InvitationStatus.PENDING,
]

export function isEditableInvitation(invitation: Invitation | null | undefined): boolean {
    if (!invitation) return false
    return EDITABLE_INVITATION_STATUSES.includes(invitation.status)
}

/**
 * Flattens an invitation's two project-ref arrays into one role-per-project
 * list — the shape every project picker in the app already speaks.
 * `projects_admin` wins if a project somehow appears in both.
 */
export function invitationProjectGrants(invitation: Invitation | null | undefined): InvitationProjectGrant[] {
    if (!invitation) return []
    const byId = new Map<string, InvitationProjectRole>()
    ;(invitation.projects_member || []).forEach((ref) => { if (ref?.id) byId.set(ref.id, 'member') })
    ;(invitation.projects_admin || []).forEach((ref) => { if (ref?.id) byId.set(ref.id, 'admin') })
    return Array.from(byId.entries()).map(([id, role]) => ({ id, role }))
}

/** The role an invitation grants on one project, or `none`. */
export function invitationRoleForProject(invitation: Invitation | null | undefined, projectId: string): InvitationProjectRole {
    return invitationProjectGrants(invitation).find((g) => g.id === projectId)?.role || 'none'
}

// ---------------- Member management requests -----------------

export enum MemberManagementRequestStatus {
    REQUESTED = 'requested',
    PENDING = 'pending',
    DONE = 'done',
    FAILED = 'failed',
}

export enum MemberManagementAction {
    ENABLE = 'enable',
    DISABLE = 'disable',
    UPDATE = 'update',
}

export class TenantMemberManagementRequest extends _FirebaseDocument {
    @Expose()
    public status: MemberManagementRequestStatus
    @Expose()
    public action: MemberManagementAction
    @Expose()
    public uid: string
    @Expose()
    public role: 'admin' | 'member' | null

    collection(): string {
        return "member_management_requests";
    }
    static fromFirebase(o: DocumentSnapshot): TenantMemberManagementRequest | null {
        return _FirebaseDocument._fromFirebase<TenantMemberManagementRequest>(TenantMemberManagementRequest, o);
    }
}

export class ProjectMemberManagementRequest extends _FirebaseDocument {
    @Expose()
    public status: MemberManagementRequestStatus
    @Expose()
    public action: MemberManagementAction
    @Expose()
    public uid: string
    @Expose()
    public role: 'admin' | 'member' | null

    collection(): string {
        return "member_management_requests";
    }
    static fromFirebase(o: DocumentSnapshot): ProjectMemberManagementRequest | null {
        return _FirebaseDocument._fromFirebase<ProjectMemberManagementRequest>(ProjectMemberManagementRequest, o);
    }
}

export class Invoice extends _FirebaseDocument {
    @Expose()
    public status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible' | 'past_due' | 'overdue'
    @Expose()
    public number: string
    @Expose()
    @Transform(anyToDate)
    public effective_at: Date
    @Expose()
    public hosted_invoice_url: string
    @Expose()
    public currency?: 'eur' | string | null
    // Stripe invoice amounts are represented in the smallest currency unit (e.g., cents)
    @Expose()
    public total?: number | null
    @Expose()
    public amount_due?: number | null

    collection(): string {
        return "invoices";
    }
    static fromFirebase(o: DocumentSnapshot): Invoice | null {
        return _FirebaseDocument._fromFirebase<Invoice>(Invoice, o);
    }
}

// ---------------- Checkout Session -----------------

export class CheckoutSessionError {
    @Expose()
    public message?: string | null
}

export class CheckoutLineItem {
    @Expose()
    public price!: string
    @Expose()
    public quantity!: number
}

export class CheckoutSession extends _FirebaseDocument {
    @Expose()
    public cancel_url?: string | null
    @Expose()
    public success_url?: string | null
    @Expose()
    public mode?: 'subscription' | string | null
    @Type(() => CheckoutLineItem)
    @Expose()
    public line_items?: CheckoutLineItem[] | null
    @Type(() => CheckoutSessionError)
    @Expose()
    public error?: CheckoutSessionError | null
    @Expose()
    public url?: string | null

    collection(): string {
        return "checkout_sessions";
    }
    static fromFirebase(o: DocumentSnapshot): CheckoutSession | null {
        return _FirebaseDocument._fromFirebase<CheckoutSession>(CheckoutSession, o);
    }
}

// ---------------- Create Checkout Session Request -----------------

export enum CheckoutSessionRequestStatus {
    REQUESTED = "requested",
    PENDING = "pending",
    DONE = "done",
    FAILED = "failed"
}

export class CheckoutSessionRequestItem {
    @Expose()
    public price_id!: string
    @Expose()
    public quantity!: number
}

export class CheckoutSessionRequest extends _FirebaseDocument {
    @Expose()
    public status: CheckoutSessionRequestStatus = CheckoutSessionRequestStatus.REQUESTED
    @Expose()
    public plan!: TenantPlan
    @Type(() => CheckoutSessionRequestItem)
    @Expose()
    public items!: CheckoutSessionRequestItem[]
    @Type(() => DocRef)
    @Expose()
    public checkout_session: DocRef | null
    @Expose()
    public error?: string | null

    collection(): string {
        return `checkout_session_requests`;
    }
    static fromFirebase(o: DocumentSnapshot): CheckoutSessionRequest | null {
        return _FirebaseDocument._fromFirebase<CheckoutSessionRequest>(CheckoutSessionRequest, o);
    }
}